import React, { useState, useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import BonusPenalty from "./settings/BonusPenalty";
import ApplicationSettings from "./settings/ApplicationSettings";
import AuditLogPage from "./settings/AuditLogPage";
import SubscriptionManagement from "./settings/SubscriptionManagement";
import PayRulesPage from "./settings/PayRulesPage";

// ============================================================================
// SETTINGS
// ----------------------------------------------------------------------------
// Account configuration: what the tenant pays, what it charges, how the app
// behaves, and the record of what everyone did.
//
// Users, Companies, Teams and User Compensation moved to Management — they are
// people, not configuration, and they are touched weekly rather than once at
// setup. See components/Management.js.
//
// Audit Log stayed here on purpose: it is a record OF the account rather than
// something you manage, and it is read in the same frame of mind as the rest of
// this page.
// ============================================================================

const tabs = [
  { id: "payment", label: "Payment Terms" },
  { id: "adjust", label: "Bonus or Penalty" },
  { id: "subscription", label: "Subscription" },
  { id: "app", label: "Application Settings" },
  { id: "audit", label: "Audit Log" }
];

// Links to the moved tabs are still in the wild — in the onboarding guide, in
// anything a Super Admin bookmarked or pasted into a message. Rather than
// dropping those people on a page that no longer has their tab, send them to
// where it went. The tab ids were kept identical so this is a straight hand-off.
const MOVED_TO_MANAGEMENT = ["user", "company", "comp", "teams"];

export default function Settings() {
  const [activeTab, setActiveTab] = useState("payment");
  const [searchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');

  useEffect(() => {
    if (requestedTab && tabs.find(tab => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  if (requestedTab && MOVED_TO_MANAGEMENT.includes(requestedTab)) {
    return <Navigate to={`/management?tab=${requestedTab}`} replace />;
  }

  return (
    <div className="max-w-full mx-auto py-4 px-2 sm:px-6 lg:px-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-1 text-gray-800">Settings</h1>
      <p className="text-sm text-gray-500 mb-6">
        Account configuration. Users, companies and teams are under Management.
      </p>
      <div className="bg-white rounded-lg shadow-md">
        {/* Tab Buttons */}
        <div className="flex flex-wrap border-b border-gray-200 bg-gray-50 rounded-t-lg">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`px-3 sm:px-6 py-3 font-medium text-sm sm:text-base focus:outline-none whitespace-nowrap
                ${
                  activeTab === tab.id
                    ? "border-b-2 border-blue-500 text-blue-600 bg-white"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }
                ${tab.id === tabs[0].id ? 'rounded-tl-lg' : ''}
                ${tab.id === tabs[tabs.length - 1].id ? 'rounded-tr-lg sm:rounded-tr-none' : ''}
              `}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {activeTab === "adjust" && <BonusPenalty />}
          {activeTab === "payment" && <PayRulesPage />}
          {activeTab === "subscription" && <SubscriptionManagement />}
          {activeTab === "app" && <ApplicationSettings />}
          {activeTab === "audit" && <AuditLogPage />}
        </div>
      </div>
    </div>
  );
}
